import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const getTokenFromHash = () => {
  const hash = window.location.hash || '';
  const queryPart = hash.split('?')[1] || '';
  return new URLSearchParams(queryPart).get('token') || '';
};

const AdminResetPassword = () => {
  const [token] = useState(getTokenFromHash);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError('This reset link is missing its token. Please request a new one from the login screen.');
  }, [token]);

  const passwordWeak = newPassword.length > 0 && (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordWeak) {
      setError('Password must be at least 8 characters and include both letters and numbers.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => { window.location.href = `${window.location.origin}${window.location.pathname}#admin-dashboard`; }, 2500);
      } else {
        setError(data.error || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4">
      <div className="bg-surface rounded-md shadow-2xl w-full max-w-md">
        <div className="p-6 bg-ink text-paper rounded-t-md">
          <h2 className="font-display text-xl text-paper">Reset Admin Password</h2>
        </div>
        <div className="p-6">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-ink">Your password has been reset. All existing sessions have been signed out.</p>
              <p className="text-ink-faint text-sm mt-2">Redirecting to login…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-ink-faint rounded-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-colors duration-200"
                    placeholder="Enter new password"
                    required
                    disabled={!token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-ink-faint hover:text-ink"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-ink-faint mt-1">At least 8 characters, with letters and numbers.</p>
              </div>

              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-ink-faint rounded-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-colors duration-200"
                    placeholder="Re-enter new password"
                    required
                    disabled={!token}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-accent-soft border border-accent/30 text-accent-deep px-4 py-3 rounded-sm text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-accent text-paper py-3 px-4 rounded-full hover:bg-accent-deep focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-md hover:shadow-lg dpc-label"
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>

              <div className="text-center">
                <a href="#admin-dashboard" className="text-accent hover:text-accent-deep text-sm font-medium transition-colors">
                  Back to login
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
